import React, { useState } from 'react';
import { useValidatePromoCodeMutation } from '../hooks/subscriptionApi';
import { PromoCodeData } from '../types/subscription';

interface PromoCodeInputProps {
  onApplyPromoCode: (promoCode: string, promoData: PromoCodeData) => void;
  onClearPromoCode: () => void;
}

const PromoCodeInput: React.FC<PromoCodeInputProps> = ({ onApplyPromoCode, onClearPromoCode }) => {
  const [code, setCode] = useState<string>('');
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [promoData, setPromoData] = useState<PromoCodeData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validatePromoCodeMutation = useValidatePromoCodeMutation();
  const isLoading = validatePromoCodeMutation.isLoading;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    setError(null);
  };

  const handleApplyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      setError('Please enter a promo code');
      return;
    }

    try {
      // If not a valid gift card, try as a regular promo code
      const result = await validatePromoCodeMutation.mutateAsync(code);
      console.log('Promo code validation result:', result);

      // Handle both array and single object response formats
      if (result.data) {
        let validatedPromoData: PromoCodeData | null = null;

        if (Array.isArray(result.data)) {
          if (result.data.length > 0) {
            validatedPromoData = result.data[0];
          }
        } else {
          // Handle direct object response
          validatedPromoData = result.data;
        }

        if (validatedPromoData && validatedPromoData.valid) {
          console.log('Valid promo data:', validatedPromoData);
          setPromoData(validatedPromoData);
          setAppliedCode(code);
          onApplyPromoCode(code, validatedPromoData);
          setCode('');
        } else {
          console.log('Invalid promo data:', validatedPromoData);
          setError('Invalid promo code or gift card');
        }
      } else {
        console.log('No data in response');
        setError('Invalid promo code or gift card');
      }
    } catch (err) {
      console.error('Error validating code:', err);
      setError('Error validating promo code or gift card');
    }
  };

  const handleClearCode = () => {
    setAppliedCode(null);
    setPromoData(null);
    onClearPromoCode();
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      {!appliedCode ? (
        <form onSubmit={handleApplyCode} style={{ display: 'flex', gap: '10px' }}>
          <input
            type='text'
            value={code}
            onChange={handleInputChange}
            placeholder='Enter promo code'
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              flex: 1,
            }}
            disabled={isLoading}
          />
          <button
            type='submit'
            disabled={isLoading}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Validating...' : 'Apply'}
          </button>
        </form>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
            backgroundColor: '#f0f9ff',
            borderRadius: '4px',
            border: '1px solid #90caf9',
          }}
        >
          <div>
            <div style={{ fontWeight: 'bold' }}>{appliedCode}</div>
            {promoData?.discountType === 'amount' && promoData?.amountOff && (
              <div>
                {}
                {`${promoData.currency?.toUpperCase()} ${(promoData.amountOff / 100).toFixed(2)}`}{' '}
                off
              </div>
            )}
            {promoData?.codeType === 'gift_card' && promoData?.balanceCents && (
              <div>Gift card balance: ${(promoData.balanceCents / 100).toFixed(2)}</div>
            )}
          </div>
          <button
            onClick={handleClearCode}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: '#dc3545',
              border: '1px solid #dc3545',
              cursor: 'pointer',
            }}
          >
            Remove
          </button>
        </div>
      )}

      {error && <div style={{ color: '#dc3545', marginTop: '5px', fontSize: '14px' }}>{error}</div>}
    </div>
  );
};

export default PromoCodeInput;
